// src/App.tsx
import { StrictMode, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Location } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import { initGA, logPageView, trackPageLoadPerformance, trackDeviceInfo } from './utils/analytics';
import { reportWebVitals } from './utils/webVitals';
import { analyticsConfig } from './utils/config';

// Import components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import TransitionLoader from './components/ui/TransitionLoader';
import SuspenseFallback from './components/ui/SuspenseFallback';
import { TransitionProvider, useTransition } from './context/TransitionContext';
import { AnimationProvider } from './context/AnimationContext';
import { smoothScrollTo } from './utils/scrollUtils';
import ErrorBoundary from './components/error/ErrorBoundary';
import { LenisProvider } from './components/providers/LenisProvider';
import { ToastProvider } from './components/providers/ToastProvider';
import AnimatedBackground from './components/ui/AnimatedBackground';
import ScrollProgress from './components/ui/ScrollProgress';


// Lazy load page components
const HomePage = lazy(() => import('./pages/home/HomePage'));
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));
const ResumePage = lazy(() => import('./pages/resume/ResumePage'));
const AboutPage = lazy(() => import('./pages/about/AboutPage'));
const WorkPage = lazy(() => import('./pages/work/WorkPage'));
const WildestProjectPage = lazy(() => import('./pages/work/WildestProjectPage'));
const KinshipProjectPage = lazy(() => import('./pages/work/KinshipProjectPage'));
const MelaleucaProjectPage = lazy(() => import('./pages/work/MelaleucaProjectPage'));
const ChanelProjectPage = lazy(() => import('./pages/work/ChanelProjectPage'));
const DellTechnologiesProjectPage = lazy(() => import('./pages/work/DellTechnologiesProjectPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Dev-only: Animation Playground (only load in development)
const AnimationPlayground = import.meta.env.DEV
  ? lazy(() => import('./pages/components/AnimationPlayground'))
  : null;

// Dev-only: Component Library (only load in development)
const ComponentLibrary = import.meta.env.DEV
  ? lazy(() => import('./pages/components/ComponentLibrary'))
  : null;

// Dev-only: Hero Opening Sequence test page (only load in development)
const HeroTestPage = import.meta.env.DEV
  ? lazy(() => import('./pages/components/HeroTestPage'))
  : null;

// Declare trackPageLoadTime if it exists, otherwise TypeScript will handle it
declare const trackPageLoadTime: (() => void) | undefined;

// ScrollToTop component with reliable scroll reset behavior
function ScrollToTop(): null {
  const { pathname, hash }: Location = useLocation();
  const { isTransitioning } = useTransition();
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle scroll behavior on navigation
  useEffect(() => {
    // Clear any pending timeouts
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // If there's a hash in the URL, scroll to that element when transition completes
    if (hash && !isTransitioning) {
      scrollTimeoutRef.current = setTimeout(() => {
        const id = hash.replace('#', '');
        smoothScrollTo(id);
      }, 100);
    }
    // If no hash and transition is complete, scroll to top
    else if (!hash && !isTransitioning) {
      scrollTimeoutRef.current = setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto'
        });

        // Double check scroll position
        scrollTimeoutRef.current = setTimeout(() => {
          if (window.scrollY > 0) {
            window.scrollTo(0, 0);
          }
        }, 50);
      }, 10);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [pathname, hash, isTransitioning]);

  return null;
}

// AppRoutes component to handle page navigation with transitions
function AppRoutes(): JSX.Element {
  const location: Location = useLocation();
  const { startTransition, endTransition, startPageAnimation } = useTransition();
  const initialLoadRef = useRef<boolean>(true);

  // Track page views when location changes
  useEffect(() => {
    if (!initialLoadRef.current) {
      logPageView(location.pathname + location.search);

      // Track page load time after page view for each navigation
      const trackPagePerformance = (): void => {
        // Use the modern API if available, otherwise fall back to the legacy method
        if (performance && typeof performance.getEntriesByType === 'function') {
          trackPageLoadPerformance();
        } else {
          trackPageLoadPerformance();
        }
      };

      // Small delay to ensure page has fully loaded
      setTimeout(trackPagePerformance, 100);
    }
  }, [location]);

  // Handle initial page load transition
  useEffect(() => {
    if (initialLoadRef.current) {
      // Skip transition for homepage and dev-only routes - go straight to content
      const DEV_ROUTES = ['/', '/components', '/animation-playground', '/hero-test'];
      if (DEV_ROUTES.includes(location.pathname)) {
        initialLoadRef.current = false;
        // Set isAnimatingIn so TextSplit can work
        startPageAnimation();
        // Still handle hash if present
        if (location.hash) {
          const id = location.hash.replace('#', '');
          setTimeout(() => {
            smoothScrollTo(id);
          }, 100);
        }
        return;
      }

      startTransition();

      // Force scroll to top immediately
      window.scrollTo(0, 0);

      // Optimize initial transition time from 1000ms to 800ms for better responsiveness
      const timer = setTimeout(() => {
        endTransition();
        initialLoadRef.current = false;

        // Handle initial hash if present
        if (location.hash) {
          const id = location.hash.replace('#', '');
          setTimeout(() => {
            smoothScrollTo(id);
          }, 100);
        }
      }, 800);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.hash, location.pathname, startTransition, endTransition]);

  return (
    <>
      <TransitionLoader />
      <Suspense fallback={<SuspenseFallback />}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
            <Route path="/about" element={<ErrorBoundary><AboutPage /></ErrorBoundary>} />
            <Route path="/contact" element={<ErrorBoundary><ContactPage /></ErrorBoundary>} />
            <Route path="/experience" element={<ErrorBoundary><ResumePage /></ErrorBoundary>} />
            <Route path="/work" element={<ErrorBoundary><WorkPage /></ErrorBoundary>} />
            <Route path="/work/wildest-design-system" element={<ErrorBoundary><WildestProjectPage /></ErrorBoundary>} />
            <Route path="/work/kinship" element={<ErrorBoundary><KinshipProjectPage /></ErrorBoundary>} />
            <Route path="/work/melaleuca" element={<ErrorBoundary><MelaleucaProjectPage /></ErrorBoundary>} />
            <Route path="/work/chanel" element={<ErrorBoundary><ChanelProjectPage /></ErrorBoundary>} />
            <Route path="/work/dell-technologies" element={<ErrorBoundary><DellTechnologiesProjectPage /></ErrorBoundary>} />
            {AnimationPlayground && (
              <Route path="/animation-playground" element={<ErrorBoundary><AnimationPlayground /></ErrorBoundary>} />
            )}
            {ComponentLibrary && (
              <Route path="/components" element={<ErrorBoundary><ComponentLibrary /></ErrorBoundary>} />
            )}
            {HeroTestPage && (
              <Route path="/hero-test" element={<ErrorBoundary><HeroTestPage /></ErrorBoundary>} />
            )}

            <Route path="*" element={<ErrorBoundary><NotFound /></ErrorBoundary>} />
          </Routes>
        </Suspense>
    </>
  );
}

function App(): JSX.Element {
  // Initialize Google Analytics with the measurement ID from config
  useEffect(() => {
    initGA(analyticsConfig.ga4MeasurementId);

    // Initialize Web Vitals monitoring
    reportWebVitals();

    // Track user environment information
    trackDeviceInfo();

    // Track initial page load time
    const handleLoad = (): void => {
      // Track page load timing on initial page load
      if (performance && typeof performance.getEntriesByType === 'function') {
        trackPageLoadPerformance();
      } else if (typeof trackPageLoadTime !== 'undefined') {
        trackPageLoadTime();
      }
    };

    // Check if the page has already loaded
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <StrictMode>
      <HelmetProvider>
        <Router>
          <AnimationProvider>
            <TransitionProvider>
              <LenisProvider>
                <ToastProvider>
                  <ScrollToTop />
                  {/* Global scroll progress indicator - outside container for proper positioning */}
                  <ScrollProgress />
                  <ErrorBoundary>
                    <div className="max-w-screen-2xl m-auto p-4 sm:p-8 md:p-16 overflow-hidden">
                      {/* Global animated background */}
                      <AnimatedBackground />

                      <Header />

                      <main className="min-h-screen-dvh">
                        <AppRoutes />
                      </main>

                      <Footer />
                    </div>
                  </ErrorBoundary>
                </ToastProvider>
              </LenisProvider>
            </TransitionProvider>
          </AnimationProvider>
        </Router>
      </HelmetProvider>
    </StrictMode>
  );
}

export default App;
